import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    LinearProgress,
    Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useQuery } from "react-query";
import { getPatientSessionSummary } from "../../../api/services/api";

interface VoiceExerciseSummaryProps {
    patientProfileId: string | number;
}

/**
 * Compact voice exercise summary card for display within session views.
 * Shows the clinician a quick overview of the patient's voice exercise
 * activity since the last session.
 */
export default function VoiceExerciseSummary({ patientProfileId }: VoiceExerciseSummaryProps) {
    const { data, isLoading, isError } = useQuery(
        ["patient-session-summary", patientProfileId],
        getPatientSessionSummary,
        { enabled: Boolean(patientProfileId) }
    );

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (isError || !data?.data) {
        return null; // Silently hide if no data
    }

    const summary = data.data;
    const { overview, modelBreakdown, recentAiSummaries } = summary;

    if (overview.totalExercises === 0) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <MicIcon color="primary" />
                        <Typography variant="h6">Voice Exercises</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        No voice exercises assigned yet.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <MicIcon color="primary" />
                    <Typography variant="h6">Voice Exercise Summary</Typography>
                    <Chip
                        label={`${overview.completionRate}% completion`}
                        size="small"
                        color={overview.completionRate >= 70 ? "success" : overview.completionRate >= 40 ? "warning" : "error"}
                    />
                </Box>

                <Alert severity="info" sx={{ mb: 2, py: 0 }} icon={<WarningAmberIcon />}>
                    <Typography variant="caption">
                        AI analysis outputs are assistive tools — not conclusive diagnoses.
                    </Typography>
                </Alert>

                {/* Stats Grid */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={3}>
                        <Box textAlign="center">
                            <Typography variant="h4" color="primary">
                                {overview.completedExercises}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Completed
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box textAlign="center">
                            <Typography variant="h4" color="error.main">
                                {overview.missedExercises}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Missed
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box textAlign="center">
                            <Typography variant="h4" color="primary">
                                {overview.totalRecordings}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Recordings
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box textAlign="center">
                            <Typography variant="h4" color="warning.main">
                                {overview.pendingReviews}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Pending Review
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Completion Bar */}
                <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            Overall Progress
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {overview.completedExercises}/{overview.totalExercises}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={overview.completionRate}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Box>

                {/* Per-Model Breakdown */}
                {modelBreakdown && modelBreakdown.length > 0 && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="subtitle2" gutterBottom>
                            By Model
                        </Typography>
                        {modelBreakdown.map((model: any) => (
                            <Box
                                key={model.modelId}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                py={0.5}
                            >
                                <Box>
                                    <Typography variant="body2">{model.modelName}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {model.schedule} · {model.completedExercises}/{model.totalExercises} done
                                    </Typography>
                                </Box>
                                {model.avgConfidence && (
                                    <Chip
                                        icon={<TrendingUpIcon />}
                                        label={`${Math.round(model.avgConfidence * 100)}%`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        ))}
                    </>
                )}

                {/* Recent AI Summaries */}
                {recentAiSummaries && recentAiSummaries.length > 0 && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="subtitle2" gutterBottom>
                            Recent AI Findings
                        </Typography>
                        {recentAiSummaries.slice(0, 3).map((item: any) => (
                            <Box key={item.exerciseId} py={0.5}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" fontWeight={500}>
                                        {item.exerciseName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(item.date).toLocaleDateString()}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {item.summary}
                                </Typography>
                                {item.confidenceScore && (
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                                        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "success.main" }} />
                                        <Typography variant="caption" color="success.main">
                                            {Math.round(item.confidenceScore * 100)}% confidence
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
